import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { adminAuthService } from '../../services/adminAuthService';
import { featureFlags } from '../../config/featureFlags';
import { toast } from 'react-toastify';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { REHYDRATE, PURGE } from 'redux-persist';
import { clearProfile } from './profileSlice';
import { clearDashboard } from './dashboardSlice';

// Types
interface User {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface AdminUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  role: string;
  department: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  permissions: string[];
  lastLogin?: string;
  createdDate: string;
  createdBy: string;
  avatarBase64?: string;
}

interface AdminLoginPayload {
  user: User;
  adminUser: AdminUser;
  token: string | null;
  refreshToken: string | null;
  type: 'JWT' | 'SESSION';
}

interface AuthState {
  user: User | null;
  adminUser: AdminUser | null; // Add adminUser to state
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  tokenExpiry: string | null;
  lastActivity: number;
  lastTokenCheck: number;
  type: 'JWT' | 'SESSION' | null;
}

interface LoginCredentials {
  username: string;
  password: string;
  otpCode?: string; // Optional OTP code for 2FA
  headers?: Record<string, string>;
}

interface LoginResponse {
  user: User;
  token: string;
  refreshToken: string;
  type: 'JWT' | 'SESSION';
}

interface RefreshTokenResponse {
  token: string;
  refreshToken: string;
  tokenExpiry: string | null;
  lastActivity: number;
  type: 'JWT' | 'SESSION';
}

interface AdminLoginResponse {
  adminUser: any;
  user: User;
  token: string | null;
  useSession: boolean;
  type: 'JWT' | 'SESSION';
}

// Helper function to decode JWT and extract expiry
const getTokenExpiry = (token: string): string | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.exp) {
      return new Date(payload.exp * 1000).toISOString();
    }
    return null;
  } catch (error) {
    console.error('Error parsing token:', error);
    return null;
  }
};

// Helper function to check if token is expired
const isTokenExpired = (tokenExpiry: string | null): boolean => {
  if (!tokenExpiry) return true;
  
  try {
    const expiryTime = new Date(tokenExpiry).getTime();
    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
    
    return now >= (expiryTime - bufferTime);
  } catch (error) {
    console.error('Error parsing token expiry:', error);
    return true; // Consider expired if we can't parse
  }
};

// Initial state
const initialState: AuthState = {
  user: null,
  adminUser: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true, // Start with loading true to prevent redirect before rehydration
  error: null,
  tokenExpiry: null,
  lastActivity: Date.now(),
  lastTokenCheck: Date.now(),
  type: null,
};

// Async thunks
export const loginUser = createAsyncThunk<
  {
    user: User;
    token: string;
    refreshToken: string | null;
    tokenExpiry: string | null;
    lastActivity: number;
    type: 'JWT' | 'SESSION';
  },
  LoginCredentials,
  {
    rejectValue: string;
    dispatch: any;
  }
>(
  'auth/login',
  async (credentials, { rejectWithValue, dispatch }) => {
    try {
      // Clear any existing profile and dashboard data before login
      dispatch(clearProfile());
      dispatch(clearDashboard());
      
      const isWebSession = credentials.headers?.['X-Type'] === 'web';
      
      const { getBrowserFingerprint } = await import('../../utils/browserFingerprint');
      const response = await fetch(`${getApiBaseUrl()}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Context': 'retail',
          ...(credentials.headers || {})
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          browserFingerprint: getBrowserFingerprint(),
          ...(credentials.otpCode && { otpCode: credentials.otpCode })
        }),
        credentials: 'include' // Always include credentials
      });

      if (!response.ok) {
        const json = await response.json();
        const detail = json.errors?.join?.(', ') || json.message || 'Login failed';
        // Check if OTP is required (401 with specific message)
        if (response.status === 401 && json.message && (json.message.includes('OTP required') || json.message.includes('OTP code'))) {
          return rejectWithValue(json.message);
        }
        if (response.status === 460 || response.status === 461 || json.code === 460 || json.code === 461) {
          return rejectWithValue(detail || 'New device detected. Check your email to confirm.');
        }
        if (response.status === 462 || response.status === 463 || json.code === 462 || json.code === 463) {
          return rejectWithValue(detail || 'New geolocation detected. Check your email to confirm.');
        }
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Login failed');
      }

      if (isWebSession) {
        // For session-based auth, we don't get tokens back
        return {
          user: {
            id: 0, // Temporary ID until we fetch the profile
            username: credentials.username,
            firstName: '',
            lastName: '',
            email: ''
          },
          token: 'SESSION_AUTH',
          refreshToken: null,
          tokenExpiry: null,
          lastActivity: Date.now(),
          type: 'SESSION'
        };
      } else {
        // JWT-based auth
        const json = await response.json();
        const data: LoginResponse = json.data;
        const tokenExpiry = getTokenExpiry(data.token);
        
        return {
          ...data,
          tokenExpiry,
          lastActivity: Date.now(),
          type: 'JWT'
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return rejectWithValue('Network error occurred');
    }
  }
);

// Utility function to clear all cookies (including non-HTTP-only ones)
const clearAllCookies = () => {
  try {
    // Get all cookies
    const cookies = document.cookie.split(';');
    
    // Clear each cookie by setting it to expire
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Clear cookie for current path
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
      
      // Also try clearing for root domain
      const domain = window.location.hostname;
      if (domain && !domain.includes('localhost') && !domain.includes('127.0.0.1')) {
        // Try with domain
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain};`;
        // Try with leading dot for subdomain support
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${domain};`;
      }
    });
    
    console.log('All cookies cleared (non-HTTP-only)');
  } catch (error) {
    console.error('Error clearing cookies:', error);
  }
};

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as { auth: AuthState };
      const token = state.auth.token;
      const tokenExpiry = state.auth.tokenExpiry;
      
      // Attempt to call logout endpoint only when it is likely to succeed.
      // If JWT is already expired, skip server logout and continue local cleanup.
      if (token && token !== 'SESSION_AUTH') {
        if (!isTokenExpired(tokenExpiry)) {
          try {
            const response = await fetch(`${getApiBaseUrl()}/auth/logout`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              credentials: 'include',
            });
            console.log('Logout API response:', response.status);
          } catch (error) {
            console.error('Logout API call error:', error);
            // Continue with cleanup even if API call fails
          }
        } else {
          console.log('Logout: JWT already expired, skipping /auth/logout call and clearing local state');
        }
      } else if (token === 'SESSION_AUTH') {
        // For session auth, try to logout via session endpoint
        try {
          const response = await fetch(`${getApiBaseUrl()}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include', // Important for session cookies
          });
          console.log('Session logout API response:', response.status);
        } catch (error) {
          console.error('Session logout API call error:', error);
          // Continue with cleanup even if API call fails
        }
      }
      
      // Small delay to ensure server has processed logout and cleared cookies
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear profile and dashboard data on logout
      dispatch(clearProfile());
      dispatch(clearDashboard());
      
      // Clear all cookies (non-HTTP-only ones that can be cleared via JavaScript)
      clearAllCookies();
      
      // Clear all localStorage items related to auth
      try {
        // Clear context-aware auth storage keys
        localStorage.removeItem('auth-retail');
        localStorage.removeItem('auth-admin');
        // Also clear legacy keys if they exist
        localStorage.removeItem('auth');
        localStorage.removeItem('persist:auth');
        localStorage.removeItem('persist:auth-retail');
        localStorage.removeItem('persist:auth-admin');
        
        // Clear any other auth-related items
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.startsWith('auth') || 
            key.includes('token') || 
            key.includes('refresh') ||
            key.includes('session')
          )) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
            console.log('Cleared localStorage key:', key);
          } catch (e) {
            console.error('Error clearing localStorage key:', key, e);
          }
        });
        
        console.log('All auth-related localStorage items cleared');
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }
      
      // Clear sessionStorage as well
      try {
        sessionStorage.clear();
        console.log('SessionStorage cleared');
      } catch (error) {
        console.error('Error clearing sessionStorage:', error);
      }
      
      // Purge redux-persist storage for auth
      try {
        const { persistor } = await import('../index');
        await persistor.purge();
        console.log('Redux-persist storage purged');
      } catch (error) {
        console.error('Error purging redux-persist storage:', error);
      }
      
      // Additional delay to ensure all cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    // Always return success to clear the store
    return;
  }
);

export const refreshToken = createAsyncThunk<RefreshTokenResponse, void>(
  'auth/refreshToken',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const refreshTokenValue = state.auth.refreshToken;
      const currentType = state.auth.type;
      
      if (!refreshTokenValue) {
        return rejectWithValue('No refresh token available');
      }

      // Determine context based on current route
      const isAdminPage = window.location.pathname.startsWith('/admin');
      const refreshUrl = isAdminPage
        ? `${getApiBaseUrl()}/admin/auth/refresh-token`
        : `${getApiBaseUrl()}/auth/refresh-token`;

      console.log('refreshToken: Refreshing token for context:', isAdminPage ? 'admin' : 'retail', 'URL:', refreshUrl);
      console.log('refreshToken: Refresh token being sent:', refreshTokenValue ? '***' + refreshTokenValue.slice(-8) : 'null');

      // CRITICAL FIX: Don't send cookies (credentials) when using JWT auth
      // Only send credentials for SESSION auth
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: refreshTokenValue }),
        credentials: currentType === 'SESSION' ? 'include' : 'omit', // Only send cookies for session auth
      });

      console.log('refreshToken: Response status:', response.status, 'ok:', response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('refreshToken: Error response:', errorText);
      }

      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Token refresh failed');
      }
      
      const data = json.data || {};
      // Support both token shapes returned by different backend auth handlers.
      const normalizedToken = data.token || data.accessToken || null;
      const normalizedRefreshToken = data.refreshToken || null;
      const tokenExpiry = normalizedToken ? getTokenExpiry(normalizedToken) : null;
      
      return {
        ...data,
        token: normalizedToken,
        refreshToken: normalizedRefreshToken,
        tokenExpiry,
        lastActivity: Date.now(),
        type: currentType as 'JWT' | 'SESSION' // We know it's one of these since we're refreshing
      };
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

export const loginAdmin = createAsyncThunk<AdminLoginPayload, { username: string; password: string; useSession: boolean; otpCode?: string }>(
  'auth/loginAdmin',
  async ({ username, password, useSession, otpCode }, { rejectWithValue }) => {
    try {
      const { getBrowserFingerprint } = await import('../../utils/browserFingerprint');
      const response = await fetch(`${featureFlags.general.apiUrl}/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Context': 'admin',
          'X-Type': useSession ? 'web' : 'api'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          username, 
          password,
          browserFingerprint: getBrowserFingerprint(),
          ...(otpCode && { otpCode })
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Check if OTP is required (401 with specific message or errors array)
        const errorMessage = errorData.message || '';
        const errorText = errorData.errors && errorData.errors.length > 0 
          ? errorData.errors.join(', ') 
          : errorMessage;
        
        if (response.status === 401 && (
          errorMessage.includes('OTP required') || 
          errorMessage.includes('OTP code') ||
          errorText.includes('OTP required') ||
          errorText.includes('OTP code') ||
          errorText.includes('Two-factor authentication')
        )) {
          return rejectWithValue(errorText || errorMessage || 'OTP required');
        }
        if (response.status === 460 || response.status === 461 || errorData.code === 460 || errorData.code === 461) {
          return rejectWithValue(errorText || 'New device detected. Check your email to confirm.');
        }
        if (response.status === 462 || response.status === 463 || errorData.code === 462 || errorData.code === 463) {
          return rejectWithValue(errorText || 'New geolocation detected. Check your email to confirm.');
        }
        return rejectWithValue(errorText || errorMessage || 'Login failed');
      }

      const result = await response.json();
      
      const adminUser: AdminUser = {
        id: username, // Use username as ID for now
        username,
        email: username, // Use username as email for now
        firstName: result.data.fullName?.split(' ')[0] || username,
        lastName: result.data.fullName?.split(' ').slice(1).join(' ') || '',
        fullName: result.data.fullName || username,
        role: result.data.role,
        department: result.data.department,
        status: 'ACTIVE',
        permissions: result.data.permissions,
        lastLogin: new Date().toISOString(),
        createdDate: new Date().toISOString(),
        createdBy: 'system',
        avatarBase64: undefined
      };

      // Map AdminUser to User type for Redux
      const user = {
        id: -1,
        username: adminUser.username,
        firstName: adminUser.fullName?.split(' ')[0] || adminUser.username,
        lastName: adminUser.fullName?.split(' ').slice(1).join(' ') || '',
        email: '',
      };

      return {
        adminUser,
        user,
        token: result.data.accessToken || null,
        refreshToken: result.data.refreshToken || null,
        type: useSession ? 'SESSION' : 'JWT'
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Admin login failed');
    }
  }
);

// Check token expiry thunk
export const checkTokenExpiry = createAsyncThunk(
  'auth/checkTokenExpiry',
  async (_, { getState, dispatch }) => {
    const state = getState() as { auth: AuthState };
    const { tokenExpiry, refreshToken: refreshTokenValue, lastTokenCheck } = state.auth;
    
    // Prevent frequent checks - only check once per minute
    const now = Date.now();
    if (now - lastTokenCheck < 60000) { // 1 minute
      return { expired: false, refreshed: false, skipped: true };
    }
    
    if (isTokenExpired(tokenExpiry)) {
      if (refreshTokenValue) {
        // Try to refresh token
        const refreshResult = await dispatch(refreshToken());
        if (refreshResult.meta.requestStatus === 'rejected') {
          // Refresh failed, logout user
          dispatch(logoutUser());
          return { expired: true, refreshed: false };
        }
        return { expired: true, refreshed: true };
      } else {
        // No refresh token, logout user
        dispatch(logoutUser());
        return { expired: true, refreshed: false };
      }
    }
    return { expired: false, refreshed: false };
  }
);

// Initialize auth from persisted state
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { getState, dispatch }) => {
    const state = getState() as { auth: AuthState };
    const { token, refreshToken: refreshTokenValue, tokenExpiry, type: authType, isAuthenticated } = state.auth;
    
    console.log('initializeAuth: Starting with state:', {
      token,
      refreshToken: refreshTokenValue,
      tokenExpiry,
      type: authType,
      isAuthenticated
    });
    
    // Handle JWT authentication
    if (authType === 'JWT' && token && refreshTokenValue) {
      console.log('initializeAuth: JWT authentication detected, checking token expiry...');
      if (isTokenExpired(tokenExpiry)) {
        console.log('initializeAuth: JWT token expired, attempting refresh...');
        // Token is expired, try to refresh
        const refreshResult = await dispatch(refreshToken());
        console.log('initializeAuth: JWT refresh result:', refreshResult.meta.requestStatus);
        if (refreshResult.meta.requestStatus === 'rejected') {
          console.log('initializeAuth: JWT refresh failed, clearing auth state');
          // Refresh failed, clear auth state
          dispatch(clearAuth());
          return { initialized: false };
        }
        console.log('initializeAuth: JWT refresh successful');
      } else {
        console.log('initializeAuth: JWT token still valid');
      }
      return { initialized: true };
    }
    
    // Handle session authentication
    if (authType === 'SESSION' && token === 'SESSION_AUTH') {
      console.log('initializeAuth: Session authentication detected, validating session...');
      // For session auth, try to validate the session
      try {
        const refreshResult = await dispatch(refreshSession());
        console.log('initializeAuth: Session validation result:', refreshResult);
        if (refreshResult.meta.requestStatus === 'fulfilled') {
          console.log('initializeAuth: Session validation successful');
          // Session is valid, the refreshSession action should have updated the state
          return { initialized: true };
        } else {
          console.log('initializeAuth: Session validation failed, clearing auth state');
          // Session validation failed, clear auth state
          dispatch(clearAuth());
          return { initialized: false };
        }
      } catch (error) {
        console.log('initializeAuth: Session validation error:', error);
        // Session validation failed, clear auth state
        dispatch(clearAuth());
        return { initialized: false };
      }
    }
    
    // Stale or unrecognized persisted auth — clear so routing can redirect to login
    if (isAuthenticated || token || refreshTokenValue) {
      console.log('initializeAuth: No valid auth credentials, clearing stale state');
      dispatch(clearAuth());
    }
    return { initialized: false };
  }
);

// Session refresh thunk for session-based authentication
export const refreshSession = createAsyncThunk(
  'auth/refreshSession',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const currentType = state.auth.type;
      
      console.log('refreshSession: Starting session validation, current type:', currentType);
      
      if (currentType !== 'SESSION') {
        console.log('refreshSession: Not using session authentication, rejecting');
        return rejectWithValue('Not using session authentication');
      }

      // Determine context based on current route
      const pathname = window.location.pathname;
      const context = pathname.startsWith('/admin') ? 'admin' : 'retail';
      console.log('refreshSession: Validating session for context:', context, 'pathname:', pathname);

      // For session refresh, we make a request to validate/extend the session
      const response = await fetch(`${getApiBaseUrl()}/auth/validate-session`, {
        method: 'GET',
        headers: {
          'X-Context': context,
        },
        credentials: 'include', // Important for session cookies
      });

      console.log('refreshSession: Response status:', response.status, 'ok:', response.ok);

      if (!response.ok) {
        const json = await response.json();
        console.log('refreshSession: Session validation failed:', json);
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Session validation failed');
      }

      const json = await response.json();
      console.log('refreshSession: Session validation successful:', json);
      
      // For session auth, we don't get new tokens, just confirmation that session is valid
      return {
        token: 'SESSION_AUTH', // Keep the session token identifier
        refreshToken: null,
        tokenExpiry: null,
        lastActivity: Date.now(),
        type: 'SESSION' as const
      };
    } catch (error) {
      console.log('refreshSession: Network error:', error);
      return rejectWithValue('Network error occurred');
    }
  }
);

// Enhanced refresh function that handles both JWT and session
export const refreshAuthentication = createAsyncThunk(
  'auth/refreshAuthentication',
  async (_, { getState, dispatch }) => {
    const state = getState() as { auth: AuthState };
    const { type: authType } = state.auth;
    
    console.log('refreshAuthentication: Current auth type:', authType);
    
    if (authType === 'SESSION') {
      // For session auth, try to refresh the session
      console.log('refreshAuthentication: Using session refresh');
      const sessionResult = await dispatch(refreshSession());
      if (sessionResult.meta.requestStatus === 'rejected') {
        console.log('refreshAuthentication: Session refresh failed');
        throw new Error('Session refresh failed');
      }
      return sessionResult.payload;
    } else if (authType === 'JWT') {
      // For JWT auth, use the existing refresh token logic
      console.log('refreshAuthentication: Using JWT token refresh');
      const jwtResult = await dispatch(refreshToken());
      if (jwtResult.meta.requestStatus === 'rejected') {
        console.log('refreshAuthentication: JWT refresh failed');
        throw new Error('JWT token refresh failed');
      }
      return jwtResult.payload;
    } else {
      console.log('refreshAuthentication: Unknown auth type:', authType);
      throw new Error('Unknown authentication type');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth: (state) => {
      state.user = null;
      state.adminUser = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.tokenExpiry = null;
      state.type = null;
    },
    setAdminUser: (state, action: PayloadAction<AdminUser | null>) => {
      state.adminUser = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    setTokens: (state, action: PayloadAction<{
      accessToken: string | null;
      refreshToken: string | null;
      type: 'JWT' | 'SESSION';
    }>) => {
      state.token = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.type = action.payload.type;
      if (action.payload.accessToken) {
        state.tokenExpiry = getTokenExpiry(action.payload.accessToken);
      }
    },
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      // Don't clear auth state on error - let the caller decide if that's needed
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.tokenExpiry = action.payload.tokenExpiry;
        state.lastActivity = action.payload.lastActivity;
        state.type = action.payload.type;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.type = null;
        state.error = action.payload || 'Login failed';
      })
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.adminUser = null; // Ensure adminUser is cleared
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.type = null;
        state.error = null;
        state.lastTokenCheck = Date.now();
        state.isLoading = false;
        toast.dismiss();
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false;
        state.adminUser = null; // Ensure adminUser is cleared on rejected logout
      })
      // Refresh token
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.tokenExpiry = action.payload.tokenExpiry;
        state.lastActivity = action.payload.lastActivity;
        state.lastTokenCheck = Date.now(); // Update lastTokenCheck to prevent immediate re-check
        state.type = action.payload.type;
        state.error = null;
        // Ensure user remains authenticated after successful refresh
        state.isAuthenticated = true;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.type = null;
        state.error = action.error.message || 'Token refresh failed';
      })
      // Admin login
      .addCase(loginAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.adminUser = action.payload.adminUser;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.tokenExpiry = action.payload.token ? getTokenExpiry(action.payload.token) : null;
        state.type = action.payload.type;
        state.lastActivity = Date.now(); // Set current time on admin login
        state.error = null;
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.adminUser = null;
        state.token = null;
        state.refreshToken = null;
        state.type = null;
        // Use action.payload since we use rejectWithValue in the thunk
        state.error = (action.payload as string) || action.error?.message || 'Admin login failed';
      })
      // Refresh Session
      .addCase(refreshSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshSession.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.isAuthenticated = true; // Mark user as authenticated after successful session validation
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.tokenExpiry = action.payload.tokenExpiry;
        state.lastActivity = action.payload.lastActivity;
        state.lastTokenCheck = Date.now(); // Update lastTokenCheck to prevent immediate re-check
        state.type = action.payload.type;
        state.error = null;
      })
      .addCase(refreshSession.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.type = null;
        state.error = action.error.message || 'Session refresh failed';
      })
      // Refresh Authentication (unified)
      .addCase(refreshAuthentication.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshAuthentication.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.tokenExpiry = action.payload.tokenExpiry;
        state.lastActivity = action.payload.lastActivity;
        state.lastTokenCheck = Date.now(); // Update lastTokenCheck to prevent immediate re-check
        state.type = action.payload.type;
        state.error = null;
        // Ensure user remains authenticated after successful refresh
        state.isAuthenticated = true;
      })
      .addCase(refreshAuthentication.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.tokenExpiry = null;
        state.type = null;
        state.error = action.error.message || 'Authentication refresh failed';
      })
      // Initialize auth handlers
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        const initialized = action.payload?.initialized === true;
        if (initialized && state.type === 'JWT' && state.token && state.refreshToken) {
          state.isAuthenticated = true;
        } else if (!initialized) {
          state.isAuthenticated = false;
          state.adminUser = null;
        }
        // Session auth: refreshSession.fulfilled sets isAuthenticated when initialized
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
      })
      // Handle redux-persist REHYDRATE action
      .addCase(REHYDRATE, (state, action: any) => {
        // When state is rehydrated from storage, keep isLoading true
        // until initializeAuth completes
        if (action.payload?.auth) {
          return {
            ...state,
            ...action.payload.auth,
            isLoading: true, // Keep loading until initializeAuth validates
          };
        }
        return state;
      });
  }
});

export const { clearAuth, setAdminUser, setTokens, updateLastActivity, setError, clearError } = authSlice.actions;
export default authSlice.reducer; 